import 'vuetify/styles'
import { createVuetify } from 'vuetify'
import { fa } from 'vuetify/iconsets/fa'
import { aliases, mdi } from 'vuetify/lib/iconsets/mdi'
import * as components from 'vuetify/components'
import * as directives from 'vuetify/directives'
import '@mdi/font/css/materialdesignicons.css'
import '@fortawesome/fontawesome-free/css/all.css'
import 'flag-icons/css/flag-icons.min.css'
import 'v-phone-input/styles'
import {
  autocompletePhoneCountryInput,
  createVPhoneInput,
  VPhoneCountryFlagSvg
} from 'v-phone-input'
import { VDateInput } from 'vuetify/labs/VDateInput'
import i18n from '@/plugins/i18n/config.js'
import { defaultThemeColors } from './themeColors'

export { defaultThemeColors } from './themeColors'

const { t } = i18n.global

export const vPhoneInput = createVPhoneInput({
  ...autocompletePhoneCountryInput,
  countryProps: {
    clearable: false,
    hideDetails: true,
    menuProps: {
      contentClass: 'v-autocomplete__content custom-dropdown v-phone-input__country__menu'
    }
  },
  countryDisplayComponent: VPhoneCountryFlagSvg,
  displayFormat: 'significant',
  label: '',
  countryLabel: '',
  defaultCountry: 'FR',
  invalidMessage: () => t('ERROR_INPUT.PHONE'),
  phoneProps: {
    bgColor: 'rgb(var(--v-theme-surface))',
    class: 'custom-input',
    variant: 'outlined'
  },
  wrapperAttrs: {
    class: 'custom-phone-input'
  }
})

const theme = {
  dark: false,
  colors: {
    primary: defaultThemeColors.primary,
    secondary: defaultThemeColors.secondary,
    error: '#FF5C5C',
    warning: '#FFAC0A',
    success: '#559a1b'
  }
}

const vuetify = createVuetify({
  locale: {
    locale: 'fr',
    fallback: 'en'
  },
  date: {
    locale: {
      fr: 'fr-FR'
    }
  },
  icons: {
    defaultSet: 'mdi',
    aliases,
    sets: {
      mdi,
      fa
    }
  },
  theme: {
    defaultTheme: 'theme',
    themes: {
      theme
    }
  },
  aliases: {
    VBtnIcon: components.VBtn,
    VBtnSecondary: components.VBtn,
    VCardModal: components.VCard,
    VCardSecondary: components.VCard,
    VDrawerFooter: components.VFooter,
    VFormCard: components.VForm,
    VPageContainer: components.VContainer
  },
  defaults: {
    VAppBar: {
      border: true,
      color: 'var(--default-background-color)',
      elevation: 0
    },
    VAutocomplete: {
      autocomplete: 'off',
      baseColor: 'grey',
      bgColor: 'rgb(var(--v-theme-surface))',
      class: 'custom-input',
      color: 'primary',
      menuProps: {
        contentClass: 'v-autocomplete__content custom-dropdown'
      },
      variant: 'outlined'
    },
    VAlert: {
      class: 'default-radius',
      variant: 'tonal'
    },
    VBtn: {
      class: 'primary-btn',
      color: 'primary',
      variant: 'flat'
    },
    VBtnIcon: {
      class: 'secondary-btn',
      color: 'grey-darken-4',
      size: 'small',
      variant: 'text'
    },
    VBtnSecondary: {
      class: 'secondary-btn text-none text-body-2',
      color: 'primary',
      size: 'large',
      variant: 'tonal'
    },
    VCard: {
      border: false,
      class: 'custom-card',
      elevation: 0
    },
    VCardModal: {
      border: false,
      class: 'custom-card modal-card pa-0 overflow-hidden d-flex flex-column',
      elevation: 0
    },
    VCardSecondary: {
      border: false,
      class: 'secondary-card',
      elevation: 0
    },
    VFormCard: {
      class: 'drawer-form-card'
    },
    VCalendar: {
      eventColor: (event) => event.color || 'rgba(var(--v-theme-primary), 0.12)',
      eventTextColor: (event) => event.textColor || 'rgb(var(--v-theme-primary))'
    },
    VCheckbox: {
      color: 'primary'
    },
    VCheckboxBtn: {
      color: 'primary'
    },
    VChip: {
      color: 'primary',
      size: 'small'
    },
    VDataTable: {
      class: 'custom-table',
      fixedFooter: true,
      fixedHeader: true,
      headerProps: {
        class: 'bg-primary text-on-primary text-no-wrap text-body-2 font-weight-bold'
      },
      itemsPerPage: -1,
      hover: true,
      sortIcon: 'mdi-chevron-right',
      sortAscIcon: 'mdi-chevron-up',
      sortDescIcon: 'mdi-chevron-down',
      noDataText: t('COMMON.NO_DATA'),
      loadingText: t('COMMON.LOADING')
    },
    VDateInput: {
      appendInnerIcon: 'mdi-calendar',
      bgColor: 'rgb(var(--v-theme-surface))',
      class: 'custom-input',
      cancelText: t('COMMON.BUTTON.CANCEL'),
      prependIcon: '',
      variant: 'outlined',
      VBtn: {
        class: 'secondary-btn',
        color: undefined,
        elevation: undefined
      }
    },
    VDatePicker: {
      color: 'primary',
      VBtn: {
        class: 'secondary-btn',
        color: undefined,
        elevation: undefined
      }
    },
    VDrawerFooter: {
      border: true,
      class: 'pa-4',
      elevation: 0
    },
    VFileInput: {
      baseColor: 'grey',
      bgColor: 'rgb(var(--v-theme-surface))',
      class: 'custom-input',
      color: 'primary',
      prependIcon: '',
      variant: 'outlined'
    },
    VFooter: {
      border: true,
      class: 'pr-6',
      color: 'var(--default-background-color)',
      elevation: 0
    },
    VMenu: {
      contentClass: 'custom-dropdown',
      offset: 6,
      VList: {
        class: 'custom-dropdown-list px-2',
        elevation: 0
      }
    },
    VOtpInput: {
      class: 'custom-input'
    },
    VPageContainer: {
      class: 'page-container',
      fluid: true
    },
    VProgressLinear: {
      color: 'secondary'
    },
    VProgressCircular: {
      color: 'secondary'
    },
    VRadio: {
      color: 'primary'
    },
    VRadioGroup: {
      color: 'primary'
    },
    VSelectionControlGroup: {
      color: 'primary'
    },
    VSelect: {
      baseColor: 'grey',
      bgColor: 'rgb(var(--v-theme-surface))',
      class: 'custom-input',
      clearable: true,
      color: 'primary',
      menuProps: {
        contentClass: 'v-select__content custom-dropdown'
      },
      variant: 'outlined'
    },
    VSwitch: {
      color: 'primary',
      inset: true
    },
    VTextarea: {
      baseColor: 'grey',
      bgColor: 'rgb(var(--v-theme-surface))',
      class: 'custom-input',
      color: 'primary',
      noResize: true,
      variant: 'outlined'
    },
    VTextField: {
      baseColor: 'grey',
      bgColor: 'rgb(var(--v-theme-surface))',
      class: 'custom-input',
      color: 'primary',
      variant: 'outlined'
    },
    VTabsWindow: {
      crossfade: false
    },
    VTabs: {
      color: 'primary'
    },
    VTabsWindowItem: {
      transition: false,
      reverseTransition: false
    },
    VWindowItem: {
      transition: false,
      reverseTransition: false
    }
  },
  components: {
    ...components,
    VDateInput
  },
  directives
})

const getCurrentThemeColors = () => {
  const themeName = vuetify.theme.global.name.value
  return vuetify.theme.themes.value?.[themeName]?.colors
}

export const setPrimarySecondaryThemeColors = (primary, secondary) => {
  const colors = getCurrentThemeColors()
  if (!colors) {
    return
  }

  colors.primary = primary
  colors.secondary = secondary
}

export const resetPrimarySecondaryThemeColors = () => {
  setPrimarySecondaryThemeColors(defaultThemeColors.primary, defaultThemeColors.secondary)
}

export default vuetify
